import { Button, CircularProgress, Divider, Grid, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useCallback, useEffect, useState } from 'react';
import { discountPackageAutocomplete } from '../../../autocomplete';
import AutocompleteSelect from '../../../components/atom/AutocompleteSelect';
import { doSwaggerCall } from '../../../hooks/useSwaggerClient';
import { useNotify } from '../../../hooks/useNotify';
import TimeshareTimeslotPicker from './TimeshareTimeslotPicker';

function DiscountLine({
    onChange,
    savedValue,
    maxValue,
    label,
    disabled,
    clearLineEnabled = false
}) {
    const [usd, setUsd] = useState(savedValue || '');
    const [usdError, setUsdError] = useState(isNaN(parseFloat(savedValue || '0')));
    const [percentage, setPercentage] = useState(
        savedValue ? (100 * (savedValue || 0)) / maxValue : ''
    );
    const [percError, setPercError] = useState(isNaN(parseFloat(savedValue || '0')));

    // recalculate percentage on maxValue change
    useEffect(() => {
        const newPerc = savedValue ? `${((savedValue * 100) / maxValue).toFixed(2)}` : '';
        setPercentage(newPerc);
    }, [setPercentage, maxValue]);

    // clear in case of discount packages
    useEffect(() => {
        if (clearLineEnabled) {
            const newPerc = savedValue ? `${((savedValue * 100) / maxValue).toFixed(2)}` : '';
            setPercentage(newPerc);
            // get value in usd from discount package here
            const newUsd = savedValue ? `${parseFloat(savedValue.toFixed(2))}` : '';
            setUsd(newUsd);
        }
    }, [setPercentage, maxValue, savedValue, clearLineEnabled]);

    function checkTrigger(isPercentageChange, newValueString) {
        if (isPercentageChange) {
            setPercentage(newValueString);
        } else {
            setUsd(newValueString);
        }
        // try to parse the new value
        const newValue = parseFloat(newValueString);
        if (isNaN(newValue)) {
            console.log('set error');
            if (isPercentageChange) {
                setPercError(true);
            } else {
                setUsdError(true);
            }
            return;
        }

        // no error during parse
        if (isPercentageChange && percError === true) {
            setPercError(false);
        } else if (usdError) {
            setUsdError(false);
        }

        // we can calculate the "other" value
        if (isPercentageChange) {
            const newUsd = ((newValue * maxValue) / 100).toFixed(2);
            // parseFloat trick to remove 0-s at the end
            setUsd(`${parseFloat(newUsd)}`);
            onChange(parseFloat(newUsd));
            return;
        }

        // usd change, need to calculate percentage
        const newPerc = ((newValue * 100) / maxValue).toFixed(2);
        // parseFloat trick to remove 0-s at the end
        setPercentage(`${parseFloat(newPerc)}`);
        onChange(newValue);
    }

    return (
        <>
            <Grid item xs={6}>
                <TextField
                    type="text"
                    label={`${label} discount (USD)`}
                    error={usdError}
                    value={usd}
                    onChange={event => {
                        checkTrigger(false, event.target.value);
                    }}
                    fullWidth
                    disabled={disabled}
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    type="text"
                    error={percError}
                    label={`${label} discount (%)`}
                    value={percentage}
                    onChange={event => {
                        checkTrigger(true, event.target.value);
                    }}
                    fullWidth
                    disabled={disabled}
                />
            </Grid>
        </>
    );
}

function DiscountAutocomplete({
    maxValue,
    updateCartItem,
    discountPackage,
    setDiscountPackage,
    discount,
    setDiscount,
    includePerItem = true,
    includeWholeCart = false,
    discountNote = '',
    setDiscountNote
}) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(true);
    const [oldPackage, setOldPackage] = useState(discountPackage?.id);
    const [timeshareActivity, setTimeshareActivity] = useState(null);
    const [timeshareTimeslot, setTimeshareTimeslot] = useState(discount?.timeshareTimeslot || null);

    const { notifySuccess, notifyError } = useNotify();

    const calculateUsdDiscount = useCallback(
        discount => {
            if (discount.type === '%') {
                // calculate % to usd
                return parseFloat(discount.amount, 10)
                    ? (maxValue * (parseFloat(discount.amount, 10) || 0)) / 100
                    : 0;
            } else if (discount.type === '$') {
                return parseFloat(discount.amount, 10) || 0;
            }
            return 0;
        },
        [maxValue]
    );

    useEffect(() => {
        if (discountPackage?.id) {
            if (oldPackage !== discountPackage.id) {
                setLoading(true);
            }
            doSwaggerCall('discountpackages', 'getDiscountPackageById', {
                discountPackageId: discountPackage.id
            })
                .then(data => {
                    setError(false);
                    setOldPackage(discountPackage.id);
                    const discountData = {
                        agencyDiscount: calculateUsdDiscount(data.discount.agencyDiscount),
                        vendorDiscount: calculateUsdDiscount(data.discount.vendorDiscount),
                        timeshareTimeslot: timeshareTimeslot,
                        timeshareActivityId: data.timeshareActivity
                            ? data.timeshareActivity.id
                            : null
                    };
                    setDiscount(discountData);

                    // Track timeshare activity if present
                    if (data.timeshareActivity) {
                        setTimeshareActivity(data.timeshareActivity);
                    } else {
                        setTimeshareActivity(null);
                    }

                    if (updateCartItem) {
                        updateCartItem({
                            discountPackage: discountPackage,
                            agencyDiscount: discountData.agencyDiscount,
                            vendorDiscount: discountData.vendorDiscount,
                            note: discountNote,
                            timeshareTimeslot: timeshareTimeslot,
                            timeshareActivityId: data.timeshareActivity
                                ? data.timeshareActivity.id
                                : null
                        });
                    }
                    // notifySuccess('Discount package applied');
                })
                .catch(err => {
                    setError(err);
                    notifyError(`Error applying discount package: ${err.message}`);
                })
                .finally(() => setLoading(false));
        }
        // Keep dependencies stable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        discountPackage,
        discountNote,
        timeshareTimeslot,
        oldPackage,
        notifyError,
        notifySuccess,
        setDiscount
    ]);

    let discountSearchFunction = discountPackageAutocomplete.search;
    if (includeWholeCart === false && includePerItem === true) {
        discountSearchFunction = discountPackageAutocomplete.searchOnlyItemLevel;
    } else if (includeWholeCart === true && includePerItem === false) {
        discountSearchFunction = discountPackageAutocomplete.searchOnlyCartLevel;
    }

    return (
        <>
            <Grid item xs={12}>
                <h4>Discount packages</h4>
            </Grid>
            {!loading && error && (
                <Grid
                    container
                    item
                    xs={12}
                    className="mt-2"
                    direction="column"
                    alignItems="center"
                >
                    <Alert severity="error">
                        <h4>{error.response?.body?.message || error.message}</h4>
                    </Alert>
                </Grid>
            )}
            {loading && !error ? (
                <Grid item xs={12}>
                    <CircularProgress />
                </Grid>
            ) : (
                <>
                    <Grid item xs={12}>
                        <AutocompleteSelect
                            initCall={discountPackageAutocomplete.init}
                            autocompleteCall={discountSearchFunction}
                            value={discountPackage.id || ''}
                            setValue={value => setDiscountPackage({ id: value })}
                            disableClearable
                        >
                            <TextField label="Discount packages" />
                        </AutocompleteSelect>
                    </Grid>
                    {discountPackage.id && (
                        <Grid item xs={12}>
                            <TextField
                                type="text"
                                label="Discount note (PLEASANT/JOURNESE BOOKING #)"
                                value={discountNote}
                                onChange={event => {
                                    setDiscountNote(event.target.value);
                                }}
                                fullWidth
                            />
                        </Grid>
                    )}
                    {discountPackage.id && timeshareActivity && (
                        <TimeshareTimeslotPicker
                            activityId={timeshareActivity.id}
                            activityName={timeshareActivity.name}
                            timeshareTimeslot={discount.timeshareTimeslot}
                            setTimeshareTimeslot={newTimeslot => {
                                setTimeshareTimeslot(newTimeslot);
                            }}
                        />
                    )}
                    {/*
                    <DiscountLine
                        label="Agency"
                        maxValue={maxValue}
                        savedValue={discount.agencyDiscount || 0}
                        onChange={newUsd => {
                            updateCartItem({
                                agencyDiscount: newUsd,
                                vendorDiscount: discount.vendorDiscount
                            });
                        }}
                        disabled
                        clearLineEnabled
                    />
                    <DiscountLine
                        label="Vendor"
                        maxValue={maxValue}
                        savedValue={discount.vendorDiscount || 0}
                        onChange={newUsd => {
                            updateCartItem({
                                agencyDiscount: discount.agencyDiscount,
                                vendorDiscount: newUsd
                            });
                        }}
                        disabled
                        clearLineEnabled
                    />
                    */}
                    <Grid item xs={12}>
                        <h5
                            onClick={() => {
                                setDiscountPackage('');
                                setDiscount({
                                    agencyDiscount: 0,
                                    vendorDiscount: 0,
                                    timeshareTimeslot: null,
                                    timeshareActivityId: null
                                });
                                setTimeshareActivity(null);
                                setTimeshareTimeslot(null);
                                updateCartItem({
                                    agencyDiscount: 0,
                                    vendorDiscount: 0,
                                    timeshareTimeslot: null,
                                    timeshareActivityId: null
                                });
                            }}
                            style={{ textAlign: 'center', cursor: 'pointer', marginTop: '10px' }}
                        >
                            Clear package
                        </h5>
                    </Grid>
                </>
            )}
        </>
    );
}

const DiscountEditor = ({
    cartItem,
    maxValue,
    updateCartItem,
    showAgencyDiscount,
    showVendorDiscount,
    discountAmountError,
    setDiscountAmountError,
    includePerItem = true,
    includeWholeCart = false,
    role
}) => {
    const savedAgencyUsd = cartItem.discount?.agencyDiscount;
    const savedVendorUsd = cartItem.discount?.vendorDiscount;
    const [isDiscountPackagesView, setIsDiscountPackagesView] = useState(
        typeof cartItem?.discount?.discountPackage?.id !== 'undefined' ||
            Object.keys(cartItem?.discount || {}).length === 0
    );
    const [discountPackage, setDiscountPackage] = useState(
        cartItem?.discount?.discountPackage || ''
    );
    const [discountNote, setDiscountNote] = useState(cartItem?.discount?.note || '');
    const [discount, setDiscount] = useState({
        agencyDiscount: cartItem?.discount?.agencyDiscount || 0,
        vendorDiscount: cartItem?.discount?.vendorDiscount || 0,
        timeshareTimeslot: cartItem?.discount?.timeshareTimeslot || null
    });

    useEffect(() => {
        setDiscountAmountError(
            (cartItem.discount?.agencyDiscount || 0) + (cartItem.discount?.vendorDiscount || 0) >
                maxValue
        );
    }, [
        cartItem.discount?.agencyDiscount,
        cartItem.discount?.vendorDiscount,
        maxValue,
        setDiscountAmountError
    ]);

    return (
        <Grid item xs={12} container>
            <Grid item container justifyContent="flex-start" spacing={2}>
                {discountAmountError && false && (
                    <>
                        <Grid item xs={12}>
                            <Alert severity="info">
                                The amount of the discount exceeds the total net value!
                            </Alert>
                        </Grid>
                    </>
                )}
                <Grid item xs={12} className="mb-2">
                    <Divider />
                </Grid>
                {window.clientData?.default_discount && cartItem.discount?.isDefault && (
                    <Grid item xs={12}>
                        <Alert
                            severity="info"
                            action={
                                (role === 'agent' || role === 'admin') && (
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={() => {
                                            // Clear discount and remove default flag
                                            setDiscountPackage('');
                                            setDiscount({
                                                agencyDiscount: 0,
                                                vendorDiscount: 0
                                            });
                                            setDiscountNote('');
                                            updateCartItem({
                                                agencyDiscount: 0,
                                                vendorDiscount: 0,
                                                note: '',
                                                isDefault: false
                                            });
                                        }}
                                    >
                                        Remove
                                    </Button>
                                )
                            }
                        >
                            Default discount applied: {window.clientData.default_discount.name}
                        </Alert>
                    </Grid>
                )}
                {(showAgencyDiscount || showVendorDiscount) && (
                    <Grid item container xs={12} justifyContent="center">
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                                setIsDiscountPackagesView(!isDiscountPackagesView);
                                setDiscountPackage('');
                                setDiscount({
                                    agencyDiscount: 0,
                                    vendorDiscount: 0,
                                    timeshareTimeslot: null
                                });
                                setDiscountNote('');
                                updateCartItem({
                                    agencyDiscount: 0,
                                    vendorDiscount: 0,
                                    note: '',
                                    timeshareTimeslot: null
                                });
                            }}
                            style={{ align: 'center', marginRight: '15px' }}
                        >
                            {isDiscountPackagesView
                                ? 'Change to Custom discounts'
                                : 'Change to Discount packages'}
                        </Button>
                    </Grid>
                )}

                {isDiscountPackagesView ? (
                    <DiscountAutocomplete
                        maxValue={maxValue}
                        updateCartItem={updateCartItem}
                        discountPackage={discountPackage}
                        setDiscountPackage={setDiscountPackage}
                        discount={discount}
                        setDiscount={setDiscount}
                        includePerItem={includePerItem}
                        includeWholeCart={includeWholeCart}
                        discountNote={discountNote}
                        setDiscountNote={setDiscountNote}
                    />
                ) : (
                    [
                        (showAgencyDiscount || showVendorDiscount) && (
                            <Grid item xs={12}>
                                <h4>Custom discounts</h4>
                            </Grid>
                        ),
                        showAgencyDiscount && (
                            <DiscountLine
                                label="Agency"
                                maxValue={maxValue}
                                savedValue={savedAgencyUsd}
                                onChange={newUsd => {
                                    updateCartItem({
                                        agencyDiscount: newUsd,
                                        vendorDiscount: cartItem.discount?.vendorDiscount,
                                        note: discountNote
                                    });
                                }}
                            />
                        ),
                        showVendorDiscount && (
                            <DiscountLine
                                label="Vendor"
                                maxValue={maxValue}
                                savedValue={savedVendorUsd}
                                onChange={newUsd => {
                                    updateCartItem({
                                        agencyDiscount: cartItem.discount?.agencyDiscount,
                                        vendorDiscount: newUsd,
                                        note: discountNote
                                    });
                                }}
                            />
                        )
                    ]
                )}
            </Grid>
        </Grid>
    );
};

export default DiscountEditor;
